import api from "./api";

export interface Document {
  admin_notes: boolean;
  description?: string;
  document_type: string;
  expiryDate?: string;
  file_path?: string;
  file_size?: number;
  id: string;
  mime_type?: string;
  name: string;
  original_filename: string;
  status: "approved" | "expired" | "pending" | "rejected" | "request-resubmission";
  stored_filename?: string;
  type: string;
  uploadDate: string;
  uploaded_at: string;
}

export interface DocumentsResponse {
  documents: Document[];
  message: string;
  success?: boolean; // Optional for backward compatibility
}

export interface DocumentUploadResponse {
  document?: {
    description?: string;
    document_type: string;
    file_size: number;
    id: string;
    original_filename: string;
    status: "approved" | "expired" | "pending" | "rejected" | "request-resubmission";
    uploaded_at: string;
  };
  message: string;
  success?: boolean;
}

export interface RequiredDocument {
  description: string;
  name: string;
  required: boolean;
  role_specific?: string[];
  type: string;
}

export interface RequiredDocumentsResponse {
  message?: string;
  required_documents: RequiredDocument[];
  success: boolean;
}

class DocumentsService {
  lastDocuments: any;
  /**
   * Delete a document
   */
  async deleteDocument(
    documentId: string,
  ): Promise<{ message: string; success: boolean }> {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  }

  /**
   * Download a document
   */
  async downloadDocument(documentId: string): Promise<Blob> {
    const response = await api.get(`/documents/${documentId}/download`, {
      responseType: "blob",
    });
    return response.data;
  }

  /**
   * Helper method to trigger file download
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download a vehicle's registration document by vehicle ID
   */
  async downloadVehicleRegistrationDocument(vehicleId: string): Promise<Blob> {
    const response = await api.get(
      `/vehicles/${vehicleId}/registration-document`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  }

  /**
   * Get documents by type
   */
  async getDocumentsByType(type: string): Promise<DocumentsResponse> {
    const response = await api.get(`/documents/type/${type}`);
    return response.data;
  }

  /**
   * Get required documents for user's role
   */
  async getRequiredDocuments(): Promise<RequiredDocumentsResponse> {
    const response = await api.get("/documents/required");
    return response.data;
  }

  /**
   * Get document status color for UI
   */
  getStatusColor(status: string): string {
    switch (status) {
      case "approved":
        return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300";
      case "expired":
        return "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300";
      case "pending":
        return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300";
      case "rejected":
        return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300";
      default:
        return "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300";
    }
  }

  /**
   * Get all user documents
   */
  async getUserDocuments(): Promise<DocumentsResponse> {
    const response = await api.get("/documents");
    return response.data;
  }

  /**
   * Map backend document types back to frontend values
   */
  mapBackendToFrontendType(
    backendType: string,
    description?: string,
    filename?: string,
  ): string {
    const reverseTypeMap: { [key: string]: string } = {
      background_check: "police_clearance",
      bank_statement: "bank_statement",
      business_license: "professional_driving_permit",
      // Contract and financial documents
      contract: "contract",
      // Backend → Frontend mapping for Vehicle Driver documents
      drivers_license: "driving_license",
      id_card: "identity_document",
      insurance: "vehicle_insurance",

      // Medical certificates can map to different frontend types based on description
      medical_certificate: "health_certificate", // Default for delivery drivers

      passport: "passport",
      tax_document: "tax_document",
      vehicle_registration: "vehicle_registration",
    };

    // Special handling for medical_certificate based on description
    if (backendType === "medical_certificate" && description) {
      if (description.includes("frontend_type:")) {
        const match = description.match(/frontend_type:([a-zA-Z_]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }

      // Detect based on content
      if (description.toLowerCase().includes("delivery_box"))
        return "delivery_box_certificate";
      if (description.toLowerCase().includes("food_handler"))
        return "food_handlers_certificate";
      if (description.toLowerCase().includes("health"))
        return "health_certificate";
    }

    // Special handling for vehicle_registration based on description
    if (backendType === "vehicle_registration" && description) {
      if (description.includes("frontend_type:")) {
        const match = description.match(/frontend_type:([a-zA-Z_]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }

      // Detect based on content
      if (description.toLowerCase().includes("scooter"))
        return "scooter_registration";
      if (description.toLowerCase().includes("bicycle"))
        return "bicycle_registration";
      if (description.toLowerCase().includes("motorbike"))
        return "scooter_registration";
    }

    // Special handling for insurance based on description
    if (backendType === "insurance" && description) {
      if (description.includes("frontend_type:")) {
        const match = description.match(/frontend_type:([a-zA-Z_]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }
    }

    // If we have a direct mapping, use it
    if (reverseTypeMap[backendType]) {
      return reverseTypeMap[backendType];
    }

    // For 'other' type, try to extract original frontend type from description
    if (backendType === "other" && description) {
      if (description.includes("frontend_type:")) {
        const match = description.match(/frontend_type:([a-zA-Z_]+)/);
        if (match && match[1]) {
          return match[1];
        }
      }

      // Fallback: try to detect from description content
      if (
        description.toLowerCase().includes("safety_gear") ||
        description.toLowerCase().includes("safety gear")
      )
        return "safety_gear_compliance";
      if (
        description.toLowerCase().includes("delivery_box") ||
        description.toLowerCase().includes("delivery box")
      )
        return "delivery_box_certificate";
      if (
        description.toLowerCase().includes("roadworthy") ||
        description.toLowerCase().includes("road worthy")
      )
        return "roadworthy_certificate";
      if (
        description.toLowerCase().includes("equipment") ||
        description.toLowerCase().includes("delivery_equipment")
      )
        return "delivery_equipment_photos";
      if (
        description.toLowerCase().includes("address") ||
        description.toLowerCase().includes("proof of address")
      )
        return "address_proof";
    }

    // Try to guess from filename if description didn't help
    const guessedType = this.guessFrontendTypeFromFilename(
      backendType,
      filename,
    );
    if (guessedType !== backendType) {
      return guessedType;
    }

    // Default fallback for 'other' type
    if (backendType === "other") {
      return "roadworthy_certificate";
    }

    return backendType;
  }

  /**
   * Replace an existing document with a new file
   */
  async replaceDocument(
    documentId: string,
    file: File,
    description?: string,
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append("document", file);

    if (description) {
      formData.append("description", description);
    }

    // Add method override for Laravel to handle PATCH with multipart data
    formData.append("_method", "PATCH");

    try {
      const response = await api.post(
        `/documents/${documentId}/replace`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error: any) {
      // Enhanced error logging for debugging
      console.error("Replace document failed:", error);
      if (error.response?.data) {
        console.error("Validation errors:", error.response.data);
        console.error("Error details:", {
          errors: error.response.data.errors,
          message: error.response.data.message,
          status: error.response.status,
        });
      }
      throw error;
    }
  }

  /**
   * Update document information
   */
  async updateDocument(
    documentId: string,
    data: Partial<Document>,
  ): Promise<DocumentUploadResponse> {
    const response = await api.put(`/documents/${documentId}`, data);
    return response.data;
  }

  /**
   * Upload a document
   */
  async uploadDocument(
    documentType: string,
    file: File,
    description?: string,
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("document_type", this.mapDocumentType(documentType));

    // Include original frontend type in description for better mapping
    const finalDescription = description
      ? `${documentType}: ${description}`
      : `${documentType}`;
    formData.append("description", finalDescription);

    const response = await api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  /**
   * Upload or replace a vehicle's registration document
   */
  async uploadVehicleRegistrationDocument(
    vehicleId: string,
    file: File,
  ): Promise<{ message: string; success: boolean }> {
    const formData = new FormData();
    formData.append("registration_document", file);
    const response = await api.post(
      `/vehicles/${vehicleId}/registration-document`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  }

  /**
   * View a vehicle's registration document by vehicle ID (returns a blob URL)
   */
  async viewVehicleRegistrationDocument(vehicleId: string): Promise<string> {
    const blob = await this.downloadVehicleRegistrationDocument(vehicleId);
    const url = window.URL.createObjectURL(blob);
    return url;
  }

  /**
   * Try to guess frontend type from filename when description is not helpful
   */
  private guessFrontendTypeFromFilename(
    backendType: string,
    filename?: string,
  ): string {
    if (!filename) return backendType;

    const lowerFilename = filename.toLowerCase();

    if (backendType === "vehicle_registration") {
      if (
        lowerFilename.includes("scooter") ||
        lowerFilename.includes("motorbike") ||
        lowerFilename.includes("bike")
      ) {
        return "scooter_registration";
      }
      if (
        lowerFilename.includes("bicycle") ||
        lowerFilename.includes("cycle")
      ) {
        return "bicycle_registration";
      }
      return "vehicle_registration"; // Default
    }

    if (backendType === "other") {
      if (
        lowerFilename.includes("safety") ||
        lowerFilename.includes("gear") ||
        lowerFilename.includes("helmet")
      ) {
        return "safety_gear_compliance";
      }
      if (lowerFilename.includes("delivery") && lowerFilename.includes("box")) {
        return "delivery_box_certificate";
      }
      if (
        lowerFilename.includes("roadworthy") ||
        lowerFilename.includes("certificate")
      ) {
        return "roadworthy_certificate";
      }
    }

    return backendType;
  }

  /**
   * Map frontend document types to backend expected values
   */
  private mapDocumentType(frontendType: string): string {
    const typeMap: { [key: string]: string } = {
      address_proof: "address_proof",
      bank_statement: "bank_statement",
      bicycle_registration: "vehicle_registration",
      contract: "contract",
      criminal_background_check: "background_check",
      delivery_box_certificate: "medical_certificate", // Food safety related
      delivery_equipment_photos: "delivery_equipment_photos",

      // Vehicle Driver documents
      driving_license: "drivers_license",
      // Delivery Driver documents
      food_handlers_certificate: "medical_certificate",
      health_certificate: "medical_certificate",

      identity_document: "id_card",
      // Common document types
      medical_certificate: "medical_certificate",
      passport: "passport",
      police_clearance: "background_check",
      professional_driving_permit: "business_license",
      roadworthy_certificate: "roadworthy_certificate",

      safety_gear_compliance: "safety_gear_compliance",
      // Scooter Driver documents
      scooter_registration: "vehicle_registration",
      tax_document: "tax_document",
      vehicle_insurance: "insurance",
      vehicle_registration: "vehicle_registration",
    };

    return typeMap[frontendType] || frontendType;
  }
}

export default new DocumentsService();
